import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Container, Stack, Grid, useTheme,
  AppBar, Toolbar, IconButton, Menu, MenuItem, Avatar, Tooltip,
  Divider, Dialog, DialogContent, DialogTitle
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import logo from '../assets/productreview.jpg';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);
  const [logoOpen, setLogoOpen] = useState(false);

  const open = Boolean(anchorEl);
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  const username = localStorage.getItem('username');

  const videoList = [
    '/assets/background.mp4',
    '/assets/background1.mp4',
  ];

  // We keep two videos and swap which one is visible
  const [current, setCurrent] = useState(0);
  const [next, setNext] = useState(1);
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false); // fade out current
      setTimeout(() => {
        setCurrent(next);
        setNext((next + 1) % videoList.length);
        setFadeIn(true); // fade in new
      }, 2000); // fade duration
    }, 8000); // change every 8 seconds
    return () => clearInterval(interval);
  }, [next, videoList.length]);

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleLogoClick = () => navigate('/admin-login');
  const handleLogoClose = () => setLogoOpen(false);

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('username');
    setAnchorEl(null);
    window.location.href = '/signin';
  };

  const handleExploreClick = () => {
    navigate(isLoggedIn ? '/shop' : '/signin');
  };

  const handleStartReviewing = () => {
    navigate('/reseller-login');
  };

  return (
    <>
      <Box sx={{ position: 'relative', minHeight: '75vh', overflow: 'hidden' }}>
        {/* Video Layer 1 */}
        <video
          autoPlay
          muted
          loop
          playsInline
          src={videoList[current]}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            opacity: fadeIn ? 1 : 0,
            transition: 'opacity 1s ease-in-out'
          }}
        />

        {/* Video Layer 2 */}
        <video
          autoPlay
          muted
          loop
          playsInline
          src={videoList[next]}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            opacity: fadeIn ? 0 : 1,
            transition: 'opacity 1s ease-in-out'
          }}
        />

        {/* Dark Overlay */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          bgcolor: 'rgba(0, 0, 0, 0.5)', zIndex: 1
        }} />

        {/* Foreground Content */}
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <AppBar position="static" sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box display="flex" alignItems="center">
                <IconButton onClick={handleLogoClick} sx={{ p: { xs: 0, sm: 1 } }}>
                  <Avatar src={logo} alt="Logo"
                    sx={{ width: { xs: 32, sm: 48 }, height: { xs: 32, sm: 48 }, border: '2px solid white' }} />
                </IconButton>
                <Typography variant="h6" sx={{
                  color: 'white', fontWeight: 'bold',
                  fontSize: { xs: '1rem', sm: '1.4rem' }, ml: 1
                }}>
                  Product Review Hub
                </Typography>
              </Box>

              <Box>
                <Tooltip title="Account Options">
                  <IconButton onClick={handleMenu} color="inherit">
                    <Box display="flex" alignItems="center">
                      {isLoggedIn && (
                        <Typography sx={{ mr: 1, color: 'white', fontWeight: 500 }}>
                          {username}
                        </Typography>
                      )}
                      <AccountCircle fontSize="large" />
                    </Box>
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {isLoggedIn ? (
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
                    </MenuItem>
                  ) : (
                    <>
                      <MenuItem
                        onClick={() => {
                          handleClose();
                          navigate('/signin');
                        }}
                      >
                        Sign In
                      </MenuItem>
                      <Divider />
                      <MenuItem
                        onClick={() => {
                          handleClose();
                          navigate('/signup');
                        }}
                      >
                        Sign Up
                      </MenuItem>
                    </>
                  )}
                </Menu>
              </Box>
            </Toolbar>
          </AppBar>

          {/* Hero Content */}
          <Container maxWidth="lg" sx={{ py: { xs: 10, md: 16 } }}>
            <Grid container spacing={6} alignItems="center" justifyContent="space-between">
              <Grid item xs={12} md={6} order={{ xs: 2, md: 2 }}
                sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                <Typography variant="h3" fontWeight="bold" gutterBottom
                  sx={{
                    textShadow: '2px 2px 8px rgba(0,0,0,0.7)',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    color: 'white',
                  }}>
                  Welcome to Product Review Hub
                </Typography>

                <Typography variant="h6" sx={{
                  opacity: 0.95, mb: 4,
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  color: '#f0f0f0',
                  textShadow: '1px 1px 4px rgba(0,0,0,0.7)',
                }}>
                  Discover honest reviews, rate products, and share your opinions with the community.
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                  justifyContent={{ xs: 'center', md: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    onClick={handleStartReviewing}
                    sx={{
                      fontWeight: 600, px: 4, py: 1.5, borderRadius: '8px',
                      boxShadow: theme.shadows[3],
                      '&:hover': { boxShadow: theme.shadows[6], transform: 'translateY(-2px)' }
                    }}>
                    Start Reviewing
                  </Button>

                  <Button variant="outlined" color="inherit" size="large"
                    onClick={handleExploreClick}
                    sx={{
                      fontWeight: 600, px: 4, py: 1.5, borderRadius: '8px',
                      borderColor: 'white', color: 'white',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', transform: 'translateY(-2px)' }
                    }}>
                    Explore Products
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} />
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default HeroSection;